import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { createUseStyles, useTheme } from 'react-jss';
import {
  Theme as theme,
  AlertService,
  EmptyStateIcon,
  NoneSelectedIcon,
  Modal,
  Button,
  ModalContent,
  ModalFooter,
  GraphIncreaseIcon,
  LoadingLine,
  LoadingSmall,
  SparkleIcon,
  MiniTooltip,
  MarketingMountainIcon,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import { ActiveOfferShiftsQuery, AwardSummaryQuery, OfferListingsByInventoryQuery } from '../../graphql/queries';
import { type InternalRefetchQueriesInclude } from '@apollo/client';
import checkFeature from '../../helpers/check-feature-flag';
import { DesirabilityPanel } from '../../components/desirability';
import featureFlags from '../../enums/feature-flags';
import { offerlistingStatuses } from '../../enums';
import { AcceptShiftedOfferListings, AwardReservePriceExceptions, AwardSuggestedOfferListingsWithUIValidation } from '../../graphql/mutations';
import { OfferComparisonStrings } from '../../string-resources';
import trucklaneChannelStatus from '../../enums/trucklane-channel-status';
import OfferCardV3 from './offer-card-v3/offer-card';
import { TrucklaneChannel, User } from '../../graphql/query-result-types';
import {
  AcceptShiftedOfferListingsArgs,
  AcceptShiftedOfferListingsResponse,
  OFFER_SHIFT_STATUS,
  OfferCardOfferListing,
  ProposedOfferShift,
} from './offer-card-v3/offer-card-types';
import OfferShiftContext, { formatShiftsForAcceptance } from './offer-shift-context';
import ShiftDestinationOfferCard from './offer-card-v3/shift-destination-offer-card';
import Tracking from '../../utilities/tracking-apis';

interface OfferListingsTheme {
  offerHeadingPadding: string;
  offerHeadingHeight: string;
  offerTransitionWrapperHeight: string;
  inventoryDescriptionFontSize: string;
  inventoryDescriptionFontWeight: string;
  inventoryDescriptionMarginBottom: string;
  inventoryDescriptionMaxWidth: string | null;
  stickyOffset: number;
  wrapperMinWidth?: number;
}

const useStyles = createUseStyles(
  {
    offerWrapper: (props: OfferListingsTheme) => ({
      minWidth: props.wrapperMinWidth,
      position: 'sticky',
      top: props.stickyOffset,
      flex: '0 0 459px',
    }),
    offerContainer: (props: OfferListingsTheme) => ({
      zIndex: 10,
      background: 'white',
      position: 'sticky',
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      top: props.stickyOffset,
      bottom: 50,
      width: '100%',
      minWidth: 459,
    }),
    offerDescription: {
      marginTop: '-1rem',
      marginBottom: 10,
    },
    offerHeading: {
      fontWeight: 400,
    },
    noOffersContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      paddingTop: '100px',
    },
    noOffersWrapper: {
      height: '100%',
    },
    noInventoryContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: 'calc(100vh - 338px)',
    },
    noOffers: {
      textAlign: 'center',
      alignSelf: 'center',
      fontSize: '1.2em',
      color: theme.text,
      fontWeight: 500,
      width: 163,
    },
    offerTransitionWrapper: (props: OfferListingsTheme) => ({
      height: props.offerTransitionWrapperHeight,
      padding: 0,
      overflowY: 'auto',
      boxShadow: `0px 10px 10px 0px rgb(255 255 255), inset 0 1px 0 0 ${theme.grey30}`,
    }),
    offerHeadingWrapper: (props: OfferListingsTheme) => ({
      zIndex: 1,
      padding: props.offerHeadingPadding,
      fontSize: '14px',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: '10px',
      fontWeight: 500,
    }),
    descriptionHeader: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    loadingLineWrapper: {
      position: 'relative',
    },
    inventoryDescription: (props: OfferListingsTheme) => ({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: '-webkit-box',
      '-webkit-line-clamp': 2,
      '-webkit-box-orient': 'vertical',
      maxWidth: props.inventoryDescriptionMaxWidth,
      fontSize: props.inventoryDescriptionFontSize,
      fontWeight: props.inventoryDescriptionFontWeight,
    }),
    inventoryDescritionTruncate: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      margin: '0px',
    },
    noInventoryHeader: {
      textAlign: 'center',
      alignSelf: 'center',
      fontSize: '1.2em',
      color: theme.text,
      fontWeight: 500,
      width: 163,
      margin: '1em 0 0 0',
    },
    noInventory: {
      textAlign: 'center',
      alignSelf: 'center',
      fontSize: '14px',
      color: theme.text,
      width: 260,
    },
    historicalOffers: {
      fontSize: 14,
      fontWeight: 400,
    },
    header: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'space-between',
      width: '100%',
    },
    historicalOffersContainer: {
      display: 'flex',
      alignItems: 'start',
      justifyContent: 'space-between',
      width: 125,
      cursor: 'pointer',
      color: theme.teal,
      stroke: theme.teal,
      '&:hover': {
        color: theme.tealDark,
        stroke: theme.tealDark,
      },
      marginLeft: '24px',
    },
    loadingContainer: {
      height: 705,
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    itemHeader: {
      width: '100%',
      height: '100%',
      display: 'flex',
      gap: '16px',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    totalSuggestedAwards: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    totalSuggestedAwardsText: {
      fontSize: '12px',
      fontWeight: '500',
      height: '16px',
      textTransform: 'uppercase',
    },
    totalSuggestedAwardsResult: {
      fontSize: '16px',
      height: '22px',
      fontWeight: 500,
    },
    buttonIcon: {
      '& svg': {
        maxWidth: 20,
        maxHeight: 20,
        width: 20,
        height: 20,
        marginTop: 2,
      },
    },
    recommendedSectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: 8,
      alignItems: 'center',
    },
    totalOpportunity: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    totalOpportunityBox: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
    },
    offerDisplayBox: {
      padding: 8,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    recommendedForAwardSection: {
      borderTop: `1px solid ${theme.grey30}`,
      extend: 'offerDisplayBox',
      background: 'linear-gradient(36deg, rgba(7, 190, 223, 0.10) -1.21%, rgba(242, 218, 81, 0.10) 88.19%, rgba(42, 178, 74, 0.10) 234.16%)',
    },
    recommendedForExceptionSection: {
      borderTop: `1px solid ${theme.grey30}`,
      extend: 'offerDisplayBox',
      background: 'linear-gradient(295deg, rgba(255, 255, 255, 0.11) 0.46%, rgba(78, 170, 193, 0.11) 100%)',
    },
    otherOffersHeading: {
      fontWeight: 500,
      fontSize: 14,
      textTransform: 'uppercase',
      padding: 8,
      margin: 0,
    },
    miniTooltipWrap: {
      width: 200,
      whiteSpace: 'wrap',
    },
  },
  { name: 'OfferListings' },
);

interface OfferListingsProps {
  inventoryId: string;
  description?: string;
  availableQuantity?: number;
  user: User;
  fullstoryTag?: string;
  stickyOffset?: number;
  trucklaneChannel: TrucklaneChannel;
  recalculationRequired?: boolean;
  recalculating?: boolean;
  isBulkAwarding?: boolean;
  additionalRefetchQueries?: InternalRefetchQueriesInclude;
  onSelectInventory?: (inventoryId: string) => void;
}

const OfferListings: React.FC<OfferListingsProps> = ({
  inventoryId,
  description,
  availableQuantity,
  user,
  fullstoryTag,
  stickyOffset,
  trucklaneChannel,
  recalculationRequired = false,
  recalculating = false,
  isBulkAwarding = false,
  additionalRefetchQueries,
  onSelectInventory,
}) => {
  const theme = useTheme<OfferListingsTheme>();
  const styles = {
    offerHeadingPadding: '16px',
    offerHeadingHeight: '98px',
    offerTransitionWrapperHeight: 'calc(100vh - 145px)',
    inventoryDescriptionFontSize: '16px',
    inventoryDescriptionFontWeight: 'normal',
    inventoryDescriptionMarginBottom: '12px',
    inventoryDescriptionMaxWidth: checkFeature(featureFlags.historicalOffers) ? '285px' : null,
    stickyOffset: stickyOffset || 0,
    theme,
  };
  const classes = useStyles(styles);
  const offerShiftContextValue = useContext(OfferShiftContext);
  const {
    data,
    loading: offerListingsLoading,
    refetch,
    error,
  } = useQuery(OfferListingsByInventoryQuery, {
    variables: { inventoryIds: [inventoryId] },
    skip: !inventoryId,
    fetchPolicy: 'cache-and-network',
  });
  const [awardSuggestedOfferListingsWithUIValidation, { loading: awardingSuggestionsLoading }] = useMutation(
    AwardSuggestedOfferListingsWithUIValidation,
  );
  const [awardReservePriceExceptionsMutation, { loading: awardingExceptionsLoading }] = useMutation(AwardReservePriceExceptions);
  const [acceptOfferShift, { loading: acceptingOfferShiftLoading }] = useMutation<AcceptShiftedOfferListingsResponse, AcceptShiftedOfferListingsArgs>(
    AcceptShiftedOfferListings,
  );
  const loading = offerListingsLoading || recalculating;

  useEffect(() => {
    if (inventoryId && trucklaneChannel?.status === trucklaneChannelStatus.VALID) refetch();
  }, [refetch, trucklaneChannel?.status]);

  const [disabled, setDisabled] = useState(false);
  const [showOfferVisualizationModal, setShowOfferVisualizationModal] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [awardingShifts, setAwardingShifts] = useState<Map<string, ProposedOfferShift>>(new Map());

  const handleOptimisticAward = useCallback((shift: ProposedOfferShift) => {
    setAwardingShifts((prev) => new Map(prev).set(shift._id, shift));
  }, []);

  const handleAwardSettled = useCallback((shiftId: string) => {
    setAwardingShifts((prev) => {
      const next = new Map(prev);
      next.delete(shiftId);
      return next;
    });
  }, []);

  const incomingShifts = useMemo(() => {
    if (offerShiftContextValue?.shiftMap.has(inventoryId)) {
      return offerShiftContextValue.shiftMap.get(inventoryId) || [];
    }
    return [];
  }, [inventoryId, offerShiftContextValue]);

  const offerListings = useMemo(() => {
    const pricePerCase = (ol: OfferCardOfferListing) => ol.totalPrice / ol.quantity;
    if (data && !loading && !error) {
      return [...data.offerListingsByInventoryQuery.offerListings].sort((a, b) => {
        if (a.status === 'AWARDED' && b.status !== 'AWARDED') return -1;
        if (a.status !== 'AWARDED' && b.status === 'AWARDED') return 1;
        if (a.status === 'ACTIVE' && b.status !== 'ACTIVE') return -1;
        if (a.status !== 'ACTIVE' && b.status === 'ACTIVE') return 1;
        if (pricePerCase(a) > pricePerCase(b)) return -1;
        if (pricePerCase(a) < pricePerCase(b)) return 1;
        return 0;
      });
    }
    return [];
  }, [data, loading, error]);

  const inventory = useMemo(() => {
    if (data?.offerListingsByInventoryQuery?.inventories) {
      return data?.offerListingsByInventoryQuery?.inventories[0];
    }
    return null;
  }, [data]);

  const reallocatedBids = useMemo((): ProposedOfferShift[] => {
    const proposed =
      incomingShifts.length > 0 && inventory
        ? (incomingShifts.filter((shift) => shift.status === OFFER_SHIFT_STATUS.PROPOSED) as ProposedOfferShift[])
        : [];
    const proposedIds = new Set(proposed.map((s) => s._id));
    const optimistic = [...awardingShifts.values()].filter((s) => !proposedIds.has(s._id));
    return [...optimistic, ...proposed];
  }, [incomingShifts, inventory, awardingShifts]);

  const renderOfferListing = useCallback(
    (offerListing: OfferCardOfferListing) => {
      const key = `offer-${offerListing._id}`;
      return (
        <div id={key} key={key}>
          <OfferCardV3
            offerInfo={offerListing}
            inventory={inventory}
            disabled={disabled}
            setOfferCardsEnabledState={setDisabled}
            key={offerListing._id}
            inventoryId={inventoryId}
            user={user}
            fullstoryTag={fullstoryTag}
            showSuggestions={!recalculationRequired}
            additionalRefetchQueries={additionalRefetchQueries}
          />
        </div>
      );
    },
    [inventory, disabled, inventoryId, user, fullstoryTag, recalculationRequired, additionalRefetchQueries],
  );

  const noListingsV2 = () => {
    return inventoryId && offerListings.length === 0 && reallocatedBids.length === 0 && data && !loading ? (
      <div className={classes.noOffersContainer}>
        <EmptyStateIcon />
        <p className={classes.noOffers}>The selected inventory has no offers</p>
      </div>
    ) : null;
  };

  const noInventory = () => {
    return !inventoryId ? (
      <div className={classes.noInventoryContainer}>
        <NoneSelectedIcon />
        <p className={classes.noInventoryHeader}>No Inventory Selected</p>
        <p className={classes.noInventory}>Select inventory on the left to view offers</p>
      </div>
    ) : null;
  };

  const noAvailibleQty = useMemo(() => {
    return availableQuantity === 0;
  }, [availableQuantity]);

  const suggestions = useMemo(() => {
    if (data?.offerListingsByInventoryQuery?.offerListings)
      return [...data.offerListingsByInventoryQuery.offerListings].filter(
        (ol) => ol.suggestions?.award?.suggested && ol.status !== offerlistingStatuses.AWARDED,
      );
    return [];
  }, [data?.offerListingsByInventoryQuery.offerListings]);

  const reservePriceExceptions = useMemo(() => {
    if (data?.offerListingsByInventoryQuery?.offerListings)
      return [...data.offerListingsByInventoryQuery.offerListings].filter(
        (ol) => ol.suggestions?.reservePriceException?.suggested && ol.status !== offerlistingStatuses.AWARDED,
      );
    return [];
  }, [data?.offerListingsByInventoryQuery.offerListings]);

  const noSuggestedAwards = useMemo(() => {
    return !(suggestions.length | reallocatedBids.length);
  }, [suggestions, reallocatedBids]);

  const noSuggestedExceptions = useMemo(() => {
    return !reservePriceExceptions.length;
  }, [reservePriceExceptions]);

  const totalSuggestedAwards = useMemo(() => {
    if (data && !loading && !error) {
      if (recalculationRequired) return <span>Recalculation Required</span>;
      if (noAvailibleQty) return <span>All Inventory Awarded</span>;
      if (noSuggestedAwards) return <span>No Suggested Awards</span>;
      const suggestionsForAwardTotal = suggestions.reduce((totalSuggestedCost, ol) => {
        if (ol.quantity === 0) return totalSuggestedCost;
        const pricePerCase = ol.totalPrice / ol.quantity;
        return totalSuggestedCost + ol.suggestions.award.quantity * pricePerCase;
      }, 0);
      const suggestionsForReallocationTotal = reallocatedBids.reduce((totalShifted, shift) => {
        return totalShifted + shift.shiftedQuantity * shift.unitPrice;
      }, 0);
      return (
        <div className={classes.totalSuggestedAwardsResult}>{accounting.formatMoney(suggestionsForAwardTotal + suggestionsForReallocationTotal)}</div>
      );
    }
    return null;
  }, [
    classes.totalSuggestedAwardsResult,
    data,
    error,
    loading,
    noAvailibleQty,
    noSuggestedAwards,
    reallocatedBids,
    recalculationRequired,
    suggestions,
  ]);

  const totalSuggestedExceptions = useMemo(() => {
    if (data && !loading && !error) {
      if (recalculationRequired) return <span>Recalculation Required</span>;
      if (noAvailibleQty) return <span>All Inventory Awarded</span>;
      if (noSuggestedExceptions) return <span>No Suggested Exceptions</span>;
      const suggestionsForRPETotal = reservePriceExceptions.reduce((totalSuggestedCost, ol) => {
        if (ol.quantity === 0) return totalSuggestedCost;
        const pricePerCase = ol.totalPrice / ol.quantity;
        return totalSuggestedCost + ol.suggestions.reservePriceException.quantity * pricePerCase;
      }, 0);
      return <div className={classes.totalSuggestedAwardsResult}>{accounting.formatMoney(suggestionsForRPETotal)}</div>;
    }
    return null;
  }, [
    classes.totalSuggestedAwardsResult,
    data,
    error,
    loading,
    noAvailibleQty,
    noSuggestedExceptions,
    recalculationRequired,
    reservePriceExceptions,
  ]);

  const awardSuggestedExceptions = useCallback(() => {
    Tracking.Event('Opportunities were awarded in offer card view', {
      userId: user._id,
      transactionCycleId: reservePriceExceptions[0]?.transactionCycleId || '',
    });
    if (reservePriceExceptions.length === 0) return;
    setDisabled(true);
    (async () => {
      try {
        const resp = await awardReservePriceExceptionsMutation({
          variables: { offerListingIds: reservePriceExceptions.map((ol) => ol._id) },
          refetchQueries: [
            { query: AwardSummaryQuery },
            { query: OfferListingsByInventoryQuery, variables: { inventoryIds: [inventoryId] } },
            ...(additionalRefetchQueries ?? []),
          ],
        });
        if (resp.data?.awardReservePriceExceptions?.errors?.length) throw Error(resp.data.awardReservePriceExceptions.errors[0].message);
      } catch {
        AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span>, dismissDelay: 5000 });
      } finally {
        setDisabled(false);
      }
    })();
  }, [awardReservePriceExceptionsMutation, additionalRefetchQueries, inventoryId, reservePriceExceptions]);

  const awardSuggestedOffers = useCallback(() => {
    setDisabled(true);
    (async () => {
      try {
        if (reallocatedBids.length > 0) {
          const acceptShiftResult = await acceptOfferShift({
            variables: {
              offerShifts: formatShiftsForAcceptance(reallocatedBids),
            },
            refetchQueries: [
              { query: AwardSummaryQuery },
              { query: ActiveOfferShiftsQuery },
              { query: OfferListingsByInventoryQuery, variables: { inventoryIds: [inventoryId] } },
              ...(additionalRefetchQueries ?? []),
            ],
          });
          if (acceptShiftResult.data?.acceptShiftedOfferListings.errors && acceptShiftResult.data.acceptShiftedOfferListings.errors.length > 0) {
            throw new Error(acceptShiftResult.data.acceptShiftedOfferListings.errors[0].message);
          }
        }
        if (suggestions.length > 0) {
          const awardSuggestedResult = await awardSuggestedOfferListingsWithUIValidation({
            variables: {
              offerListingIds: suggestions.map((ol) => ol._id),
              trucklaneGroupChannelId: trucklaneChannel._id,
              trucklaneGroupChannelUpdatedAt: trucklaneChannel.updatedAt,
            },
          });
          if (awardSuggestedResult.data?.awardSuggestedOfferListingsWithUIValidation?.errors?.length)
            throw Error(awardSuggestedResult.data.awardSuggestedOfferListingsWithUIValidation.errors[0].message);
        }
      } catch {
        AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span>, dismissDelay: 5000 });
      } finally {
        setDisabled(false);
      }
    })();
  }, [
    acceptOfferShift,
    additionalRefetchQueries,
    awardSuggestedOfferListingsWithUIValidation,
    inventoryId,
    reallocatedBids,
    suggestions,
    trucklaneChannel,
  ]);

  const renderedReallocatedBids = useMemo(() => {
    if (reallocatedBids.length > 0 && inventory) {
      return reallocatedBids.map((shift: ProposedOfferShift) => {
        return (
          <ShiftDestinationOfferCard
            key={shift._id}
            originalOfferListing={shift.originalOfferListing}
            setOfferCardsDisabled={setDisabled}
            inventoryId={inventoryId}
            shift={shift}
            inventory={inventory}
            listing={shift.listing}
            activeTruckLane={shift.activeTruckLane}
            buyerSite={shift.buyerSite}
            user={user}
            fullstoryTag={fullstoryTag}
            showSuggestions={!recalculationRequired}
            additionalRefetchQueries={additionalRefetchQueries}
            onSelectInventory={onSelectInventory}
            onOptimisticAward={handleOptimisticAward}
            onAwardSettled={handleAwardSettled}
          />
        );
      });
    }
    return null;
  }, [
    recalculationRequired,
    fullstoryTag,
    reallocatedBids,
    inventory,
    inventoryId,
    user,
    additionalRefetchQueries,
    onSelectInventory,
    handleOptimisticAward,
    handleAwardSettled,
  ]);

  // Hoisted out of renderedOfferListings so the Opportunity section's visibility can gate the rest
  // of the page. The branches are exclusive: a listing suggested for award renders there even when
  // it also carries an exception suggestion, which is why this is narrower than the
  // `reservePriceExceptions` list that drives the Award Opportunities mutation.
  const groups = useMemo(() => {
    return offerListings.reduce(
      (gr, ol: OfferCardOfferListing) => {
        if (ol.suggestions?.award?.suggested && ol.status !== offerlistingStatuses.AWARDED) gr.recommendedAwards.push(ol);
        else if (ol.suggestions?.reservePriceException?.suggested && ol.status !== offerlistingStatuses.AWARDED) gr.recommendedExceptions.push(ol);
        else gr.other.push(ol);
        return gr;
      },
      { recommendedAwards: [], recommendedExceptions: [], other: [] },
    );
  }, [offerListings]);

  const showSuggestedForException = groups.recommendedExceptions.length > 0;

  const renderedOfferListings = useMemo(() => {
    const boxes = [];
    const awardSuggestionsButton = (
      <Button
        style={{ alignSelf: 'center', height: '36px' }}
        iq
        icon={SparkleIcon as React.FC<{ style?: React.CSSProperties; className?: string }>}
        disabled={recalculationRequired || noAvailibleQty || noSuggestedAwards}
        onClick={awardSuggestedOffers}
        loading={awardingSuggestionsLoading || acceptingOfferShiftLoading || isBulkAwarding}
        loadingText="Awarding Suggestions..."
        className={classes.buttonIcon}
      >
        Award Suggestions
      </Button>
    );
    const awardExceptionsButton = (
      <Button
        style={{ alignSelf: 'center', height: '36px' }}
        iq
        icon={SparkleIcon as React.FC<{ style?: React.CSSProperties; className?: string }>}
        disabled={recalculationRequired || noAvailibleQty || noSuggestedExceptions}
        onClick={awardSuggestedExceptions}
        loading={awardingExceptionsLoading || isBulkAwarding}
        loadingText="Awarding Opportunities..."
        className={classes.buttonIcon}
      >
        Award Opportunities
      </Button>
    );
    const showSuggestedForAward = groups.recommendedAwards.length > 0 || renderedReallocatedBids;
    if (showSuggestedForAward) {
      boxes.push(
        <div key="recommended" className={classes.recommendedForAwardSection}>
          <div className={classes.recommendedSectionHeader}>
            <div>
              <div className={classes.totalSuggestedAwardsText}>Suggested For Award</div>
              {totalSuggestedAwards}
            </div>
            {recalculationRequired ? (
              <MiniTooltip text={<div className={classes.miniTooltipWrap}>You must recalculate suggested awards to award.</div>}>
                {awardSuggestionsButton}
              </MiniTooltip>
            ) : (
              awardSuggestionsButton
            )}
          </div>
          {renderedReallocatedBids}
          {groups.recommendedAwards.map((ol: OfferCardOfferListing) => renderOfferListing(ol))}
        </div>,
      );
    }
    if (showSuggestedForException) {
      boxes.push(
        <div key="recommended-exceptions" className={classes.recommendedForExceptionSection}>
          <div className={classes.recommendedSectionHeader}>
            <div className={classes.totalOpportunityBox}>
              <MarketingMountainIcon style={{ marginRight: 4 }} />
              <div className={classes.totalOpportunity}>
                <div className={classes.totalSuggestedAwardsText}>Opportunity</div>
                {totalSuggestedExceptions}
              </div>
            </div>
            {awardExceptionsButton}
          </div>
          {groups.recommendedExceptions.map((ol: OfferCardOfferListing) => renderOfferListing(ol))}
        </div>,
      );
    }
    if (groups.other.length > 0) {
      boxes.push(
        <div key="other" className={classes.offerDisplayBox}>
          {showSuggestedForAward && <h3 className={classes.otherOffersHeading}>Other Offers</h3>}
          {groups.other.map((ol: OfferCardOfferListing) => renderOfferListing(ol))}
        </div>,
      );
    }
    return boxes;
  }, [
    groups,
    showSuggestedForException,
    recalculationRequired,
    noAvailibleQty,
    noSuggestedAwards,
    noSuggestedExceptions,
    awardSuggestedOffers,
    awardSuggestedExceptions,
    awardingSuggestionsLoading,
    awardingExceptionsLoading,
    acceptingOfferShiftLoading,
    isBulkAwarding,
    classes,
    renderedReallocatedBids,
    totalSuggestedAwards,
    totalSuggestedExceptions,
    renderOfferListing,
  ]);

  const dataViz = () => {
    return (
      checkFeature(featureFlags.historicalOffers) &&
      description &&
      !loading && (
        <div className={classes.historicalOffersContainer} onClick={() => setShowOfferVisualizationModal(true)}>
          <GraphIncreaseIcon />
          <span className={classes.historicalOffers}>Historical Offers</span>
        </div>
      )
    );
  };

  const itemHeader = () => {
    return (
      <div className={classes.itemHeader}>
        <div className={classes.descriptionHeader}>
          <div data-testid="inventory-description" className={classes.inventoryDescription}>
            <p className={classes.inventoryDescritionTruncate} data-testid="description-text">
              {description && !loading ? description : null}
            </p>
          </div>
          {dataViz()}
        </div>
        {/* Reuses the existing `inventory` memo. The !loading guard stops the panel flashing in
            before the query resolves. Tied to the Opportunity section: the reasons only explain a
            price the supplier is being asked to accept, so with no exception on offer there is
            nothing for them to explain away. */}
        {!loading && showSuggestedForException && checkFeature(featureFlags.lowDesirabilityFlag) && (
          <DesirabilityPanel desirability={inventory?.desirability} />
        )}
      </div>
    );
  };

  const onHide = () => {
    setIframeLoading(true);
    setShowOfferVisualizationModal(false);
  };

  const offerVizDashboard = () => {
    if (data && !loading && !error)
      return (
        <>
          <Modal onHide={onHide} open={showOfferVisualizationModal} closeOnEsc closeOnOutsideClick staticHeight={true}>
            <ModalContent>
              <iframe
                title="Offer Visualization Dashboard"
                src={data.offerListingsByInventoryQuery.offerVisualizationUrl}
                style={{ height: 735, width: 820, border: 'none', display: (!iframeLoading && 'block') || 'none' }}
                onLoad={() => setIframeLoading(false)}
                allow="clipboard-read; clipboard-write"
              />
              {iframeLoading && (
                <div className={classes.loadingContainer}>
                  <LoadingSmall />
                </div>
              )}
            </ModalContent>
            <ModalFooter>
              <Button type="button" onClick={onHide} secondary>
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </>
      );
    return null;
  };

  const loadingMessage = useMemo(() => {
    return (
      <div className={classes.noOffersContainer}>
        <p className={classes.noOffers}>LOADING OFFERS...</p>
      </div>
    );
  }, [classes]);

  return (
    <div className={classes.offerWrapper}>
      <div className={classes.offerContainer} data-testid="offer-container">
        <div className={classes.loadingLineWrapper}>
          <LoadingLine loading={loading} position="top" />
          <div className={classes.offerHeadingWrapper}>{itemHeader()}</div>
        </div>
        <div
          key={inventoryId}
          className={offerListings.length ? classes.offerTransitionWrapper : classes.noOffersWrapper}
          data-testid="offer-transition-wrapper"
        >
          {offerVizDashboard()}
          {loading ? loadingMessage : renderedOfferListings}
          {noListingsV2()}
          {noInventory()}
        </div>
      </div>
    </div>
  );
};

export default OfferListings;
